import React from "react";
import {
	Video,
	PhoneCall,
	Mic,
	MicOff,
	VideoOff,
	Monitor,
	MoreVertical,
	Users,
	MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ViewTitle } from "@/components/ui/view-title";
import type { Channel } from "@/utils/custom-types";

export interface MeetViewProps {
	workspaceId: string;
	channelId?: string;
	channel?: Channel | null;
	session?: any;
	currentWorkspace?: any;
}

export const MeetView: React.FC<MeetViewProps> = ({
	workspaceId,
	channelId,
	channel,
	session,
	currentWorkspace,
}) => {
	return (
		<div className="flex h-full flex-col bg-background">
			{/* Header */}
			<ViewTitle
				icon={Video}
				title={channel?.title || "Meeting Room"}
				description="Video conferencing and collaboration"
				channelId={channelId}
				workspaceId={workspaceId}
			/>

			{/* Content Area */}
			<div className="flex flex-1 overflow-hidden bg-black">
				<div className="flex flex-1 flex-col">
					{/* Video Grid */}
					<div className="flex flex-1 items-center justify-center p-4">
						<div className="grid h-full w-full max-w-6xl grid-cols-2 gap-4 lg:grid-cols-3">
							{/* Participant 1 - Speaking */}
							<div className="relative overflow-hidden rounded-lg bg-gray-900">
								<div className="absolute inset-0 flex items-center justify-center">
									<div className="h-24 w-24 rounded-full bg-primary/20" />
								</div>
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
									<div className="flex items-center justify-between">
										<span className="font-medium text-white text-sm">
											Alice Johnson
										</span>
										<div className="flex items-center gap-1">
											<div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
											<MicOff className="h-4 w-4 text-white/60" />
										</div>
									</div>
								</div>
								<div className="absolute inset-0 rounded-lg ring-2 ring-primary" />
							</div>

							{/* Participant 2 */}
							<div className="relative overflow-hidden rounded-lg bg-gray-900">
								<div className="absolute inset-0 flex items-center justify-center">
									<div className="h-24 w-24 rounded-full bg-chart-1/20" />
								</div>
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
									<div className="flex items-center justify-between">
										<span className="font-medium text-white text-sm">
											Bob Smith
										</span>
										<div className="flex items-center gap-1">
											<Mic className="h-4 w-4 text-white/60" />
										</div>
									</div>
								</div>
							</div>

							{/* Participant 3 - Video Off */}
							<div className="relative overflow-hidden rounded-lg bg-gray-900">
								<div className="absolute inset-0 flex flex-col items-center justify-center">
									<div className="h-24 w-24 rounded-full bg-chart-2/20" />
									<VideoOff className="mt-2 h-6 w-6 text-white/40" />
								</div>
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
									<div className="flex items-center justify-between">
										<span className="font-medium text-white text-sm">
											Carol Davis
										</span>
										<div className="flex items-center gap-1">
											<Mic className="h-4 w-4 text-white/60" />
										</div>
									</div>
								</div>
							</div>

							{/* Participant 4 */}
							<div className="relative overflow-hidden rounded-lg bg-gray-900">
								<div className="absolute inset-0 flex items-center justify-center">
									<div className="h-24 w-24 rounded-full bg-chart-3/20" />
								</div>
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
									<div className="flex items-center justify-between">
										<span className="font-medium text-white text-sm">
											David Wilson
										</span>
										<div className="flex items-center gap-1">
											<MicOff className="h-4 w-4 text-white/60" />
										</div>
									</div>
								</div>
							</div>

							{/* Your Video */}
							<div className="relative overflow-hidden rounded-lg bg-gray-900 lg:col-span-2">
								<div className="absolute inset-0 flex items-center justify-center">
									<div className="h-32 w-32 rounded-full bg-primary/30" />
								</div>
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
									<div className="flex items-center justify-between">
										<span className="font-medium text-white text-sm">You</span>
										<div className="flex items-center gap-1">
											<Mic className="h-4 w-4 text-white/60" />
										</div>
									</div>
								</div>
								<div className="absolute right-3 top-3 rounded bg-black/50 px-2 py-1 text-white text-xs">
									HD
								</div>
							</div>
						</div>
					</div>

					{/* Meeting Controls */}
					<div className="border-t border-gray-800 bg-gray-900 p-4">
						<div className="mx-auto flex max-w-6xl items-center justify-between">
							{/* Left - Meeting Info */}
							<div className="flex items-center gap-4">
								<div>
									<p className="font-medium text-white text-sm">
										{channel?.title || "Team Meeting"}
									</p>
									<p className="text-gray-400 text-xs">
										Meeting ID: 123-456-789
									</p>
								</div>
							</div>

							{/* Center - Controls */}
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="icon"
									className="rounded-full border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
								>
									<Mic className="h-5 w-5" />
								</Button>
								<Button
									variant="outline"
									size="icon"
									className="rounded-full border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
								>
									<Video className="h-5 w-5" />
								</Button>
								<Button
									variant="outline"
									size="icon"
									className="rounded-full border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
								>
									<Monitor className="h-5 w-5" />
								</Button>
								<Button variant="destructive" className="rounded-full px-6">
									<PhoneCall className="mr-2 h-4 w-4" />
									End Call
								</Button>
								<Button
									variant="outline"
									size="icon"
									className="rounded-full border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
								>
									<MoreVertical className="h-5 w-5" />
								</Button>
							</div>

							{/* Right - Actions */}
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									className="border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
								>
									<Users className="mr-2 h-4 w-4" />5
								</Button>
								<Button
									variant="outline"
									size="sm"
									className="border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
								>
									<MessageSquare className="mr-2 h-4 w-4" />
									Chat
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
